import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { dataSourceLinkSelectionSchema } from "../data-source-link-selection";

/**
 * Connection-keyed DataSource Adapter for blob storage.
 *
 * Keyed by the source *connection* (not the file format), and provider-agnostic:
 * it works with any `blob`-kind connection (Azure Blob Storage, AWS S3 — see
 * `DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS` in `@erp/shared-types`). The
 * provider comes from the configured DataSource Link's DataSource connection,
 * not from step config. It reads the connection + credential + pull config from
 * the Link's DataSource, discovers the source file(s), and emits their URIs for
 * a downstream format-keyed DataSource Writer. It does not fetch, stage, or
 * parse the files — the writer reads the source directly and interprets it with
 * the Link's parse config.
 *
 * Which Link(s) it operates on is chosen via the shared selection config
 * (`dataSourceLinkStrategy`):
 *   - 'static'          — one specific DataSource Link.
 *   - 'matching_schema' — every DataSource Link conforming to a chosen
 *     SchemaDefinition. The adapter discovers each matched Link's file(s) using
 *     that Link's own connection + pull config and emits every (file, link)
 *     pair, fanning out downstream across all of them.
 */
export const blobStorageDataSourceAdapterStep = defineStepConfig({
  type: "blob_storage_datasource_adapter",
  name: "Blob Storage DataSource Adapter",
  description:
    "Connect to a DataSource's cloud blob storage (Azure Blob Storage or AWS S3 — determined by the DataSource Link's connection) with its credential and discover the source file(s) for a DataSource Link, emitting their URIs for a downstream DataSource Writer.",
  category: "DataSource Adapter",

  experimental: {
    reason:
      "Blob Storage DataSource Adapter is under active development and not yet validated for production workflows.",
  },

  inputSchema: z.object({}),

  outputSchema: z.object({
    // Two index-aligned arrays (blobStorageUris[i] is written into
    // dataSourceLinks[i]). Wire both into a downstream DataSource Writer: the
    // writer carries a hard-coded `batch` config, so the engine lockstep-zips
    // them into one (uri, link) pair per run. The 1:1 length invariant is
    // enforced at runtime in the handler, not by this schema.
    blobStorageUris: z
      .array(z.string())
      .describe(
        "URIs of the discovered source files, index-aligned 1:1 with dataSourceLinks.",
      ),
    dataSourceLinks: z
      .array(z.string().uuid())
      .describe(
        "DataSource Link for each discovered file, index-aligned 1:1 with blobStorageUris.",
      ),
  }),

  configSchema: dataSourceLinkSelectionSchema,

  emits: [] as const,

  // Downstream per-link fanout children run as DETACHED fire-and-forget child
  // workflows, each with its own Temporal execution-timeout budget (the Data
  // Ingest default — see DATA_INGEST_WORKFLOW_EXECUTION_TIMEOUT). Without
  // this, all N ingest loads execute inside the parent run and share its
  // single workflowExecutionTimeout — a multi-link pull (e.g. 20
  // matching_schema links) times out collectively even though no individual
  // load exceeds its own activity timeout.
  detachedFanout: true,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});
